import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Search, ChevronDown, MapPin, Loader2, X } from "lucide-react";
import { Button } from "./Button";
import { Input } from "./input";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { getAllCountries, getCountryById } from "../../redux/Actions/AdminAction";
import { detectUserLocation } from "../../redux/Actions/AuthAction";
import { getCachedCountryPreference, setCachedCountryPreference } from "../../lib/currencyUtils";

interface Country {
    id: string;
    name: string;
    code: string;
}

interface CountryDropdownProps {
    selectedCountry?: Country | null;
    selectedCountries?: Country[];
    onCountrySelect?: (country: Country | null) => void;
    onCountriesSelect?: (countries: Country[]) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    autoDetect?: boolean; // New prop to enable auto-detection
    multiple?: boolean; // New prop to enable multiple selection
    placement?: "auto" | "top" | "bottom"; // New prop to control dropdown placement
}

const CountryDropdown: React.FC<CountryDropdownProps> = ({
    selectedCountry,
    selectedCountries = [],
    onCountrySelect,
    onCountriesSelect,
    placeholder = "Select a country...",
    className = "",
    disabled = false,
    autoDetect = false,
    multiple = false,
    placement = "bottom",
}) => {
    const dispatch = useAppDispatch();
    const { countries, loading } = useAppSelector((state: any) => state.admin || {
        countries: [],
        loading: false,
    });

    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [isDetectingCountry, setIsDetectingCountry] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [internalSelectedCountry, setInternalSelectedCountry] = useState<Country | null>(null);
    const [internalSelectedCountries, setInternalSelectedCountries] = useState<Country[]>([]);
    const [dropdownPosition, setDropdownPosition] = useState<"top" | "bottom">("bottom");
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Load 10 countries initially
    useEffect(() => {
        dispatch(getAllCountries({
            page: 1,
            limit: 10,
            search: "",
            isShowLoader: false,
        }));
    }, [dispatch]);

    // Country detection and caching (only if autoDetect is enabled)
    useEffect(() => {
        const detectAndSelectCountry = async () => {
            if (!autoDetect || !countries || countries.length === 0) return;
            
            setIsDetectingCountry(true);
            try {
                // First check for cached preference
                const cachedCountryId = getCachedCountryPreference();
                if (cachedCountryId) {
                    const cachedCountry = countries.find(country => country.id === cachedCountryId);
                    if (cachedCountry) {
                        setInternalSelectedCountry(cachedCountry);
                        onCountrySelect?.(cachedCountry);
                        setIsDetectingCountry(false);
                        return;
                    }
                }

                // Use backend location detection to get country code
                const result = await dispatch(detectUserLocation({ isShowLoader: false }));
                
                if (detectUserLocation.fulfilled.match(result)) {
                    const locationData = result.payload;
                    const countryCode = locationData.countryCode;
                    const countryName = locationData.countryName;
                    
                    // Find country by code in the loaded countries
                    let detectedCountry = countries.find((country: any) => 
                        country.initial?.toUpperCase() === countryCode ||
                        country.code?.toUpperCase() === countryCode
                    );
                    
                    // If not found by code, try by name
                    if (!detectedCountry && countryName) {
                        detectedCountry = countries.find((country: any) => 
                            country.name?.toLowerCase() === countryName.toLowerCase()
                        );
                    }
                    
                    // If still not found in loaded countries, search for specific country and append
                    if (!detectedCountry) {
                        // Try searching by country code first
                        const searchResult = await dispatch(getAllCountries({
                            page: 1,
                            limit: 1,
                            search: countryCode,
                            isShowLoader: false,
                            append: true, // Append to existing list instead of replacing
                        }));
                        
                        if (getAllCountries.fulfilled.match(searchResult)) {
                            const searchedCountries = searchResult.payload?.data || searchResult.payload || [];
                            // Try by code first
                            detectedCountry = searchedCountries.find((country: any) => 
                                country.initial?.toUpperCase() === countryCode ||
                                country.code?.toUpperCase() === countryCode
                            );
                            
                            // If still not found by code, try searching by name
                            if (!detectedCountry && countryName) {
                                const nameSearchResult = await dispatch(getAllCountries({
                                    page: 1,
                                    limit: 1,
                                    search: countryName,
                                    isShowLoader: false,
                                    append: true,
                                }));
                                
                                if (getAllCountries.fulfilled.match(nameSearchResult)) {
                                    const nameSearchedCountries = nameSearchResult.payload?.data || nameSearchResult.payload || [];
                                    detectedCountry = nameSearchedCountries.find((country: any) => 
                                        country.name?.toLowerCase() === countryName.toLowerCase()
                                    );
                                }
                            }
                        }
                    }
                    
                    if (detectedCountry) {
                        setInternalSelectedCountry(detectedCountry);
                        onCountrySelect?.(detectedCountry);
                        setCachedCountryPreference(detectedCountry.id);
                    }
                }
            } catch (error) {
                console.error("Error detecting country:", error);
            } finally {
                setIsDetectingCountry(false);
            }
        };
        
        detectAndSelectCountry();
    }, [autoDetect, countries.length, dispatch]); // Removed onCountrySelect from dependencies

    // Sync with external props
    useEffect(() => {
        if (multiple && selectedCountries !== undefined) {
            setInternalSelectedCountries(selectedCountries);
        } else if (!multiple && selectedCountry !== undefined) {
            setInternalSelectedCountry(selectedCountry);
        }
    }, [selectedCountry?.id, JSON.stringify(selectedCountries), multiple]); // Use JSON.stringify to detect array changes

    // Handle search with backend query and local filtering
    useEffect(() => {
        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // If search query is empty, don't search
        if (!searchQuery.trim()) {
            setIsSearching(false);
            return;
        }

        // Debounce search - wait 500ms after user stops typing
        setIsSearching(true);
        searchTimeoutRef.current = setTimeout(async () => {
            try {
                // Search backend for countries matching the query
                await dispatch(getAllCountries({
                    page: 1,
                    limit: 10,
                    search: searchQuery.trim(),
                    isShowLoader: false,
                    append: true, // Append results to existing list
                }));
            } catch (error) {
                console.error("Error searching countries:", error);
            } finally {
                setIsSearching(false);
            }
        }, 500);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery, dispatch]);

    // Client-side filtering of countries based on search query
    const filteredCountries = useMemo(() => {
        if (!searchQuery.trim()) {
            return countries;
        }
        
        const query = searchQuery.toLowerCase();
        return countries.filter((country: Country) => 
            country.name.toLowerCase().includes(query) ||
            (country.code && country.code.toLowerCase().includes(query))
        );
    }, [countries, searchQuery]);

    // Handle country selection
    const handleCountrySelect = useCallback((country: Country) => {
        if (multiple) {
            const isSelected = internalSelectedCountries.some(c => c.id === country.id);
            let newSelectedCountries;
            
            if (isSelected) {
                // Remove country if already selected
                newSelectedCountries = internalSelectedCountries.filter(c => c.id !== country.id);
            } else {
                // Add country if not selected
                newSelectedCountries = [...internalSelectedCountries, country];
            }
            
            setInternalSelectedCountries(newSelectedCountries);
            onCountriesSelect?.(newSelectedCountries);
            
            // Don't close dropdown in multiple mode
        } else {
            setInternalSelectedCountry(country);
            onCountrySelect?.(country);
            setIsOpen(false);
            setSearchQuery("");
            
            // Cache the selected country
            if (autoDetect) {
                setCachedCountryPreference(country.id);
            }
        }
    }, [multiple, internalSelectedCountries, onCountriesSelect, onCountrySelect, autoDetect]);

    // Handle clear selection
    const handleClear = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        if (multiple) {
            setInternalSelectedCountries([]);
            onCountriesSelect?.([]);
        } else {
            setInternalSelectedCountry(null);
            onCountrySelect?.(null);
        }
    }, [multiple, onCountriesSelect, onCountrySelect]);

    // Calculate dropdown position based on available space
    useEffect(() => {
        if (isOpen && dropdownRef.current && placement === "auto") {
            const rect = dropdownRef.current.getBoundingClientRect();
            const dropdownHeight = 320; // max-h-80 (approximately 320px)
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;

            // If not enough space below but enough space above, open upward
            if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
                setDropdownPosition("top");
            } else {
                setDropdownPosition("bottom");
            }
        } else if (placement === "top") {
            setDropdownPosition("top");
        } else {
            setDropdownPosition("bottom");
        }
    }, [isOpen, placement]);

    // Handle dropdown toggle
    const toggleDropdown = useCallback(() => {
        if (disabled) return;
        setIsOpen(!isOpen);
        if (!isOpen) {
            // Focus search input when opening
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    }, [disabled, isOpen]);

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {/* Trigger Button */}
            <button
                type="button"
                onClick={toggleDropdown}
                disabled={disabled || isDetectingCountry}
                className={`
					w-full flex items-center justify-between px-3 py-2 border rounded-lg
					${disabled || isDetectingCountry
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white hover:border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    }
					transition-colors
				`}
            >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    {isDetectingCountry ? (
                        <>
                            <Loader2 className="h-4 w-4 animate-spin text-blue-600 flex-shrink-0" />
                            <span className="text-gray-500">Detecting location...</span>
                        </>
                    ) : multiple ? (
                        internalSelectedCountries.length > 0 ? (
                            <div className="flex items-center gap-1 flex-wrap">
                                <MapPin className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                {internalSelectedCountries.length <= 2 ? (
                                    internalSelectedCountries.map(country => (
                                        <span key={country.id} className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                            {country.name}
                                        </span>
                                    ))
                                ) : (
                                    <>
                                        <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                            {internalSelectedCountries[0].name}
                                        </span>
                                        <span className="text-sm text-gray-500">
                                            +{internalSelectedCountries.length - 1} more
                                        </span>
                                    </>
                                )}
                            </div>
                        ) : (
                            <span className="text-gray-500">{placeholder}</span>
                        )
                    ) : internalSelectedCountry ? (
                        <>
                            <MapPin className="h-4 w-4 text-gray-500 flex-shrink-0" />
                            <span className="truncate">{internalSelectedCountry.name}</span>
                            <span className="text-gray-400 text-sm">({internalSelectedCountry.code})</span>
                        </>
                    ) : (
                        <span className="text-gray-500">{placeholder}</span>
                    )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                    {((multiple && internalSelectedCountries.length > 0) || (!multiple && internalSelectedCountry)) && !isDetectingCountry && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                        >
                            <X className="h-3 w-3 text-gray-400" />
                        </button>
                    )}
                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </div>
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className={`absolute z-50 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-hidden ${
                    dropdownPosition === "top" ? "bottom-full mb-1" : "top-full mt-1"
                }`}>
                    {/* Search Input */}
                    <div className="p-3 border-b border-gray-200">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Search countries..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                    </div>

                    {/* Countries List */}
                    <div className="max-h-60 overflow-y-auto">
                        {(loading && countries.length === 0) || isSearching ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                                <span className="ml-2 text-gray-500">
                                    {isSearching ? "Searching countries..." : "Loading countries..."}
                                </span>
                            </div>
                        ) : filteredCountries.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                                <MapPin className="h-8 w-8 text-gray-300 mb-2" />
                                <p>No countries found</p>
                                {searchQuery && (
                                    <p className="text-sm text-gray-400">Try a different search term</p>
                                )}
                            </div>
                        ) : (
                            <>
                                {filteredCountries.map((country: Country) => {
                                    const isSelected = multiple 
                                        ? internalSelectedCountries.some(c => c.id === country.id)
                                        : internalSelectedCountry?.id === country.id;
                                    
                                    return (
                                        <button
                                            key={country.id}
                                            type="button"
                                            onClick={() => handleCountrySelect(country)}
                                            className={`
                                                w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-gray-50
                                                ${isSelected ? "bg-blue-50 border-l-4 border-blue-500" : ""}
                                                transition-colors
                                            `}
                                        >
                                            <div className="flex-shrink-0">
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                                    isSelected ? "bg-blue-100" : "bg-gray-200"
                                                }`}>
                                                    <MapPin className={`h-4 w-4 ${
                                                        isSelected ? "text-blue-600" : "text-gray-500"
                                                    }`} />
                                                </div>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className={`font-medium truncate ${
                                                    isSelected ? "text-blue-900" : "text-gray-900"
                                                }`}>
                                                    {country.name}
                                                </p>
                                                <p className={`text-sm truncate ${
                                                    isSelected ? "text-blue-600" : "text-gray-500"
                                                }`}>{country.code}</p>
                                            </div>
                                            {multiple && isSelected && (
                                                <div className="flex-shrink-0">
                                                    <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                                                        <span className="text-white text-xs">✓</span>
                                                    </div>
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CountryDropdown;
